<script>
import loadGoogleMapsApi from 'load-google-maps-api'

const defaultEndpoint = 'https://rawp-api.domiagroup.com/v1/cities?input='
const defaultHeaders = {}

function normalize (text) {
    const noAccent = text.normalize("NFD").replace(/[\u0300-\u036f]/g, '')
    return noAccent.replace(/\s\s+/g, ' ').toLowerCase().trim()
}

function extractRepetition (numberAndRepetition) {
    let number = ''
    let repetition = ''

    ;[...numberAndRepetition].forEach((c) => {
        if (/\d/.test(c) && !repetition.length) {
            number += c
        } else {
            repetition += c
        }
    })

    return {
        number: number.trim(),
        repetition: repetition.trim(),
    }
}

function slugify (text) {
    if (!text) { return '' }
    return text
        .toString()
        // split an accented letter in the base letter and the acent
        .normalize( 'NFD' )
        // remove all previously split accents
        .replace( /[\u0300-\u036f]/g, '' )
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        // eslint-disable-next-line no-useless-escape
        .replace(/[^\w\-]+/g, '')
        // eslint-disable-next-line no-useless-escape
        .replace(/\-\-+/g, '-')
}

export default {
    name: 'BaseAddress',

    props: {
        value: {
            type: String,
        },

        types: {
            type: Array,
            default: () => []
        },

        restrictions: {
            type: Object,
            default: () => {
                return {
                    'country': ['fr']
                }
            }
        }
    },

    data () {
        return {
            autocomplete: undefined,
            focused: false,
            uid: undefined,
            loading: false,
        }
    },

    computed: {
    },

    mounted() {
        loadGoogleMapsApi({
            key: 'AIzaSyCWsbZxeOvV8ZjBUglp2xumatSzuXCpANQ',
            libraries: ['places'],
        }).then((googleMaps) => {
            const input = this.$refs['searchTextField']
            const options = {
                types: this.types
            }

            this.autocomplete = new googleMaps.places.Autocomplete(
                input,
                options
            )

            this.autocomplete.setComponentRestrictions(this.restrictions)

            this.autocomplete.setFields(['address_components', 'formatted_address'])

            googleMaps.event.addListener(this.autocomplete, 'place_changed', async () => {
                const place = this.autocomplete.getPlace()

                let parsedComponents = this.parseComponents(place.address_components)

                const { cities } = await this.fetchCities(parsedComponents.googleZipcode || parsedComponents.googleCityName)

                const city = this.getBestCity(cities, parsedComponents.googleCityName)

                parsedComponents.city = city

                this.$emit('input', {
                    currentTarget: {
                        value: place.formatted_address
                    }
                })
                this.$emit('addressChange', parsedComponents)
            })
        })

        const inBrowser = typeof window !== 'undefined'
        if (inBrowser) {
            this.uid = 'address' + Math.random()
        }
    },

    methods: {
        fetchCities(input) {
            const endpoint = defaultEndpoint
            const headers = defaultHeaders

            return fetch(`${endpoint}${input}`, { headers })
                .then((response) => response.json())
                .then((json) => json)
        },

        getBestCity (cities, cityName) {
            if (cities.length === 1) {
                return cities[0]
            }

            return cities.find((city) => {
                return slugify(city.name) === slugify(cityName)
            })
        },

        parseComponents (googleComponents) {
            if (!googleComponents) { return undefined }

            const components = {}

            // repetition
            const streetNumberRaw = googleComponents.find((component) => {
                return component.types.includes('street_number')
            })

            if (streetNumberRaw) {
                const streetNumberNormalized = normalize(streetNumberRaw.short_name)

                const { number, repetition } = extractRepetition(streetNumberNormalized)

                components.repetition = repetition
                components.number = number
            }

            // route type
            const routeRaw = googleComponents.find((component) => {
                return component.types.includes('route')
            })

            if (routeRaw) {
                const routeNormalized = normalize(routeRaw.short_name)

                components.routeType = routeNormalized.split(' ')[0]

                // route name
                components.routeName = routeNormalized.replace(components.routeType, '').trim()
            }

            // city name
            const localityRaw = googleComponents.find((component) => {
                return component.types.includes('locality')
            })

            if (localityRaw) {
                components.googleCityName = localityRaw.long_name
            }

            // zipcode
            const postalCodeRaw = googleComponents.find((component) => {
                return component.types.includes('postal_code')
            })

            if (postalCodeRaw) {
                components.googleZipcode = postalCodeRaw.short_name
            }

            // country
            const countryRaw = googleComponents.find((component) => {
                return component.types.includes('country')
            })

            if (countryRaw) {
                components.country = countryRaw.long_name
            }

            return components
        }
    },
}
</script>

<template>
    <div class="base-address base-input"
      :class="{
        'has-value': value && value.length,
        'focused': focused
      }">
        <label :for="`${uid}`">
            <div class="label-text">
                <slot name="label"/>
            </div>
        </label>
        <div class="input-wrapper">
            <div class="before">
                <slot name="before"/>
            </div>
            <input
              ref="searchTextField"
              @focus="focused = true"
              @blur="focused = false"
              v-on="$listeners"
              :value="value"
              :id="`${uid}`"
              v-bind="$attrs">
            <div class="after">
                <slot name="after"/>
            </div>
        </div>
    </div>

</template>
<style lang="stylus">
// @import '../assets/css/base.styl'

.base-input.base-address
    font-size 1.4em
    height 3.8em
    display block
    position relative
    border-bottom 2px solid $AkGreyscaleMediumUltGrey
    cursor text

    .label-text
    input
        padding-left .4em
        padding-right .4em

    label
        position absolute
        top 0
        left 0
        right 0
        bottom 0
        cursor text
        z-index 1

    .label-text
        transition ease .2s all
        display block
        position absolute
        top 50%
        transform translateY(-50%)
        font-size inherit
        color $AkGreyscaleBlack

    .input-wrapper
        display flex
        align-items center
        opacity 0
        background transparent
        position absolute
        top 50%
        width 100%
        transform translateY(-20%)
        font-size inherit

        input
            flex-grow 1
            flex-shrink 1
            padding-top 0
            padding-bottom 0
            border 0
            min-width 0
            box-sizing border-box
            color $AkGreyscaleBlack
            font-family inherit
            font-size inherit

            &::placeholder
                transition color ease 0s
                color transparent

            &:focus
                outline 0

                &::placeholder
                    transition color ease .3s .1s
                    color var(--AkGreyscaleMediumLtGrey)
    &.focused
    &.has-value
        label
            z-index 0

        .label-text
            top 0px
            font-weight bold
            transform translateY(0)
            font-size 0.7em
            padding-left 0
            padding-right 0
            color var(--mainColor)

        .input-wrapper
            opacity 1

    &.focused
        border-color var(--mainColor)

    &.has-errors
        border-color $AkMajorCoral

        .label-text
            color $AkMajorCoral

    &.base-input-small
        font-size 1.15em

</style>